import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Frame from '../../components/Onboarding/Frame';
import { ArrowLeft, ArrowRight, Check, Crosshair } from 'react-feather';
import CircularProgress from '@mui/material/CircularProgress';
import moment from 'moment';
import { fetchExams, getExamLectures } from '../../services/exams';
import { RootState } from '../../store/store';
import { hapticsImpactLight } from '../../utils/haptics';
import {
  stopImpersonating,
  getOriginalAdminSession,
  updateCurrentUserInfo,
} from '../../services/auth';
import {
  getComprehensiveFlashcards,
  getComprehensiveQbank,
  getHighYieldFlashcards,
  getHighYieldQbank,
  getStandardFlashcards,
  getStandardQbank,
  isStepExam,
  isVerifiedSchoolExam,
  isShelfExam,
  isCustomSchoolExam,
} from '../../utils/examUtils';
import { createAssents, VERSIONS } from '../../services/assent';
import { AssentType } from '../../types/Assent';
import { Capacitor } from '@capacitor/core';
import GoalSelection from '../../components/Global/GoalSelection';
import { TopicTreeNode } from '../../types/TopicCustomization';
import OraSwitch from '../../components/CustomMUI/Switch';
import {
  fetchTopicTree,
  updateTopicTree,
} from '../../services/topicCustomization';
import { setActiveFilters } from '../../slices/activeTopics/activeTopicsSlice';
import { flattenTopicTree } from '../../utils/flattenTopicTree';
import { cloneDeep } from 'lodash';
import { updateUserSelfAssessment } from '../../services/users';
import { useUserExamUpdate } from '../../utils/updateUserExam';
import LectureScheduleEditor from '../../components/ExamEditor/LectureScheduleEditor';
import SchoolExamInfo from '../../components/ExamEditor/SchoolExamInfo';
import SchoolSelector from '../../components/Global/SchoolSelector';
import { animate, transition } from '../../helpers/animationHelpers';
import useWindowWidth from '../../hooks/useWindowWidth';
import ThreeOrb from '../../components/Home/ThreeOrb';
import posthog from 'posthog-js';
import { examsSelectors } from '../../slices/exams/examsSlice';
import { lecturesSelectors } from '../../slices/lectures/lecturesSlice';
import CustomExamDatePicker from '../../components/Global/CustomExamDatePicker';
import ExamBlockBuilder from '../../components/Onboarding/ExamBlockBuilder';
import ExamSelect from '../../components/Global/ExamSelect';
import { SubscriptionAccess, SubscriptionPlan } from '../../types/Subscription';
import { handleCheckout } from '../../services/subscriptions';
import { useDarkMode } from '../../hooks/useDarkMode';

const Onboarding: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const userSchool = useSelector((state: RootState) => state.school.school);

  const availableExams = useSelector(examsSelectors.selectAll);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const lectures = useSelector(lecturesSelectors.selectAll);

  const parentOnboarding = useRef<HTMLDivElement>(null);
  const [userSelectedGoal, setUserSelectedGoal] = useState<
    'High Yield' | 'Standard' | 'Comprehensive' | ''
  >('');

  const [hideFlashLoader, setHideFlashLoader] = useState(false);
  const [availableTopics, setAvailableTopics] = useState<TopicTreeNode[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<TopicTreeNode[]>([]);
  const [strengths, setStrengths] = useState('');
  const [weaknesses, setWeaknesses] = useState('');
  const inputHeading = useRef<HTMLHeadingElement>(null);
  const inputDiv1 = useRef<HTMLDivElement>(null);
  const inputDiv2 = useRef<HTMLDivElement>(null);
  const hrRef = useRef<HTMLHRElement>(null);
  const autoSection = useRef<HTMLDivElement>(null);
  const auto1 = useRef<HTMLDivElement>(null);
  const auto2 = useRef<HTMLDivElement>(null);
  const auto3 = useRef<HTMLDivElement>(null);
  const [streamTitleIsDone, setStreamTitleIsDone] = useState(false);

  const [isImpersonating, setIsImpersonating] = useState(false);

  // --- School Exam Flow ---
  const [isCreatingSchoolExam, setIsCreatingSchoolExam] = useState(false);
  const newSchoolExamCreated = useMemo(
    () => availableExams.some((exam) => isCustomSchoolExam(exam)),
    [availableExams]
  );

  // ONBOARDING SCREENS
  const frameIdToStepName: { [key: number]: string } = {
    0: 'exam_selection',
    1: 'exam_date',
    2: 'goal_selection',
    3: 'topic_selection',
    4: 'strengths_weaknesses',
    6: 'choose_school',
    7: 'build_school_exam',
    8: 'add_lectures',
    9: 'cohort_exam_details',
    10: 'exam_blocks',
    100: 'final_loader',
  };

  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const stepSequence = [0, 1, 2, 3, 4, 100];
  const shelfSequence = [0, 1, 2, 4, 100];
  const schoolExamSequence = [0, 7, 8, 100];
  const peerAdvocateSequence = [6, 10, 100];
  const personalizedSequence = [0, 9, 100];
  const [frameSequence, setFrameSequence] = useState(stepSequence);
  // Track viewed frame IDs
  const [viewedFrames, setViewedFrames] = useState<Set<number>>(new Set());

  const { updateExam } = useUserExamUpdate(undefined, {
    showSuccessMessage: false,
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  useDarkMode();
  const windowWidth = useWindowWidth();

  // Check if we're impersonating
  useEffect(() => {
    const originalSession = getOriginalAdminSession();
    setIsImpersonating(!!originalSession);
  }, []);

  const handleStopImpersonating = async () => {
    await stopImpersonating();
    setIsImpersonating(false);
    navigate('/admin');
  };

  // Track frame views for PostHog
  useEffect(() => {
    const currentFrameId = frameSequence[currentFrameIndex];

    // Ensure we have a valid frame ID and it hasn't been viewed yet
    if (
      currentFrameId !== undefined &&
      !viewedFrames.has(currentFrameId) &&
      frameIdToStepName[currentFrameId]
    ) {
      const stepName = frameIdToStepName[currentFrameId];
      posthog?.capture('onboarding_frame_view', {
        frame_number: currentFrameId,
        frame_name: stepName,
      });

      // Add the current frame ID to the set of viewed frames
      // We only want to capture the frame view event once
      setViewedFrames((prevViewedFrames) => {
        const newViewedFrames = new Set(prevViewedFrames);
        newViewedFrames.add(currentFrameId);
        return newViewedFrames;
      });
    }
  }, [currentFrameIndex, frameSequence, viewedFrames]);

  useEffect(() => {
    if (currentUser?.buyIntent && !Capacitor.isNativePlatform()) {
      const parts = currentUser.buyIntent.split('_');
      const product = `${parts[0]}_${parts[1]}` as SubscriptionAccess;
      const plan = parts[2] as SubscriptionPlan;
      if (product && plan) {
        handleCheckout(product, plan);
      }
      clearBuyIntent();
    }
    if (currentUser && currentUser.ambassador) {
      createInitialAssents();
      setFrameSequence(peerAdvocateSequence);
      setHideFlashLoader(true);
      fetchExams();
    } else if (currentUser && currentUser.isOnboarded) {
      navigate('/');
    } else {
      setHideFlashLoader(true);
      createInitialAssents();
      fetchExams();
    }
  }, []);

  const clearBuyIntent = async () => {
    await updateCurrentUserInfo({
      buyIntent: null,
    });
  };

  // Set primary exam and exam selection
  useEffect(() => {
    setStrengths('');
    setWeaknesses('');

    // Handle school exam creation flow
    if (isCreatingSchoolExam) {
      setSelectedTopics([]);
      setAvailableTopics([]);
      setFrameSequence(schoolExamSequence);
      return;
    }

    // Handle exam selection flow
    if (!primaryExam) {
      return;
    }

    setIsCreatingSchoolExam(false);

    // Set frame sequence based on exam type
    if (isStepExam(primaryExam)) {
      loadExamTopics(primaryExam.id);
      setFrameSequence(stepSequence);
    } else if (isShelfExam(primaryExam)) {
      setSelectedTopics([]);
      setAvailableTopics([]);
      setFrameSequence(shelfSequence);
    } else if (isVerifiedSchoolExam(primaryExam)) {
      setFrameSequence(personalizedSequence);
    }
  }, [primaryExam?.id, isCreatingSchoolExam]);

  // Fetch lectures for custom exam
  useEffect(() => {
    if (
      primaryExam?.id &&
      primaryExam.type === 'CUSTOM' &&
      !currentUser?.ambassador
    ) {
      getExamLectures(primaryExam.id);
    }
  }, [primaryExam?.id]);

  // Navigate to home if user is already onboarded
  useEffect(() => {
    if (currentUser) {
      if (currentUser.isOnboarded) {
        navigate('/');
      }
    }
  }, [currentUser]);

  // Set user selected goal based on exam's content quantities
  useEffect(() => {
    if (!primaryExam?.id) return;

    const {
      selectedVignetteCount: qbankCount,
      selectedFlashcardCount: flashcardCount,
    } = primaryExam;
    if (!qbankCount || !flashcardCount) return;

    const matchesQuantities = (qbank: number, flashcards: number) =>
      qbankCount === qbank && flashcardCount === flashcards;

    if (
      matchesQuantities(
        getHighYieldQbank(primaryExam),
        getHighYieldFlashcards(primaryExam)
      )
    ) {
      setUserSelectedGoal('High Yield');
    } else if (
      matchesQuantities(
        getComprehensiveQbank(primaryExam),
        getComprehensiveFlashcards(primaryExam)
      )
    ) {
      setUserSelectedGoal('Comprehensive');
    } else if (
      matchesQuantities(
        getStandardQbank(primaryExam),
        getStandardFlashcards(primaryExam)
      )
    ) {
      setUserSelectedGoal('Standard');
    }
  }, [primaryExam?.id]);

  // Update exam content quantities when goal changes
  useEffect(() => {
    if (!userSelectedGoal || !primaryExam || !currentUser) return;

    const goalQuantities = {
      'High Yield': {
        qbank: getHighYieldQbank(primaryExam),
        flashcards: getHighYieldFlashcards(primaryExam),
      },
      Standard: {
        qbank: getStandardQbank(primaryExam),
        flashcards: getStandardFlashcards(primaryExam),
      },
      Comprehensive: {
        qbank: getComprehensiveQbank(primaryExam),
        flashcards: getComprehensiveFlashcards(primaryExam),
      },
    };

    const quantities =
      goalQuantities[userSelectedGoal as keyof typeof goalQuantities];

    // Only update if the quantities are different from the current values
    if (
      quantities.flashcards !== primaryExam.selectedFlashcardCount ||
      quantities.qbank !== primaryExam.selectedVignetteCount
    ) {
      updateExam({
        userId: currentUser.id,
        examId: primaryExam.id,
        selectedFlashcardCount: quantities.flashcards,
        selectedVignetteCount: quantities.qbank,
      });
    }
  }, [userSelectedGoal]);

  // Stream Title of Current Frame is 0
  useEffect(() => {
    if (currentFrameIndex === 0) {
      streamTitle(streamTitleIsDone);
    }
    if (currentFrameIndex === 1 || frameSequence[currentFrameIndex] === 9) {
      setStreamTitleIsDone(true);
    }
  }, [currentFrameIndex, streamTitleIsDone, frameSequence]);

  const streamTitle = async (streamTitleIsDone: boolean) => {
    const examSelectionContainer = document.querySelector(
      '.exam-selection-container'
    );
    const streamTarget = document.getElementById('stream-target');
    if (windowWidth > 560) {
      if (!streamTitleIsDone) {
        if (streamTarget && examSelectionContainer) {
          await transition(streamTarget as HTMLElement, {
            opacity: '1',
          });
          await animate(streamTarget as HTMLElement, 'streaming-text');
          await transition(streamTarget as HTMLElement, {
            transform: 'translateY(0) scale(1)',
          });
          await transition(examSelectionContainer as HTMLElement, {
            opacity: '1',
          });
        }
      } else {
        if (streamTarget) {
          streamTarget.classList.add('done');
        }
      }
    } else {
      if (streamTarget && examSelectionContainer) {
        (streamTarget as HTMLElement).style.transition = 'none';
        (examSelectionContainer as HTMLElement).style.transition = 'none';
        (streamTarget as HTMLElement).style.opacity = '1';
        (examSelectionContainer as HTMLElement).style.opacity = '1';
        (streamTarget as HTMLElement).style.transform =
          'translateY(0) scale(1)';
        (streamTarget as HTMLElement).style.clipPath = 'none';
      }
    }
  };

  // Trigger End Onboarding if Frame is 100
  useEffect(() => {
    if (frameSequence[currentFrameIndex] === 100) {
      (async () => {
        if (!currentUser) {
          console.error('No current user found');
          return;
        }
        if (weaknesses.trim()) {
          await Promise.all([
            updateUserSelfAssessment(
              currentUser.id,
              strengths.trim(),
              weaknesses.trim()
            ),
            triggerLoadingUI(),
          ]);
        } else {
          setHideFlashLoader(false);
          await wait(500);
        }
        finishOnboarding();
      })();
    } else if (frameSequence[currentFrameIndex] === 0) {
      if (primaryExam && isCustomSchoolExam(primaryExam)) {
        updatePrimaryExam(primaryExam.id, false);
      }
    }
  }, [frameSequence, currentFrameIndex]);

  const cohortExams = useMemo(() => {
    return availableExams.filter(
      (exam) => exam.type === 'CUSTOM' && exam.verified
    );
  }, [availableExams]);

  // If the user has already started creating a school exam, set it as primary when they click 'School Exam'
  useEffect(() => {
    if (isCreatingSchoolExam) {
      const customExam = availableExams.find(
        (exam) => exam.type === 'CUSTOM' && !exam.verified
      );
      if (customExam) {
        updatePrimaryExam(customExam.id);
      }
    }
  }, [isCreatingSchoolExam]);

  const handleExamSelect = async (examId: string) => {
    setIsCreatingSchoolExam(false);
    if (!currentUser || !examId) return;
    updateExam({
      userId: currentUser.id,
      examId: examId,
      primary: true,
    });
    setCurrentFrameIndex(currentFrameIndex + 1);
  };

  const handleAddExam = async () => {
    // If a custom exam was already created, switch to it instead of creating a new one
    if (newSchoolExamCreated) {
      const customExamId = availableExams.find((exam) =>
        isCustomSchoolExam(exam)
      )?.id;
      if (customExamId) updatePrimaryExam(customExamId);
    }
    setIsCreatingSchoolExam(true);
    setCurrentFrameIndex(currentFrameIndex + 1);
  };

  const updatePrimaryExam = async (
    examId: string,
    setPrimary: boolean = true
  ) => {
    if (!currentUser || !examId) return;
    await updateExam({
      userId: currentUser.id,
      examId: examId,
      primary: setPrimary,
    });
  };

  const updateExamDate = async (examDate: Date | null) => {
    if (!currentUser || !primaryExam || !examDate) return;
    await updateExam({
      userId: currentUser.id,
      examId: primaryExam.id,
      examDate: examDate.toISOString(),
    });
  };

  const triggerLoadingUI = async () => {
    await wait(500);
    inputHeading.current?.classList.add('reveal');

    await wait(500);
    inputDiv1.current?.classList.add('reveal');

    await wait(1000);
    inputDiv2.current?.classList.add('reveal');

    await wait(1000);
    hrRef.current?.classList.add('reveal');

    await wait(1000);
    autoSection.current?.classList.add('reveal');

    // Finish loading curriculum UI
    await wait(2000);
    auto1.current?.classList.add('done');

    // Finish generating study session
    await wait(2000);
    auto2.current?.classList.add('done');

    await wait(1000);
  };

  const wait = (ms: number | undefined) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const loadExamTopics = async (examId: string) => {
    const response = await fetchTopicTree(examId);
    if (response) {
      const topicsWithActiveState = response.map((topic: TopicTreeNode) => ({
        ...topic,
        active: true,
      }));

      setAvailableTopics(topicsWithActiveState);
      setSelectedTopics(topicsWithActiveState);
    }
  };

  const createInitialAssents = async () => {
    if (!currentUser) return;
    const assents = [
      {
        type: AssentType.tos,
        version: VERSIONS.tos,
        dateAccepted: moment().toISOString(),
        location: 'signup',
      },
      {
        type: AssentType.privacyPolicy,
        version: VERSIONS.privacyPolicy,
        dateAccepted: moment().toISOString(),
        location: 'signup',
      },
    ];
    await createAssents(currentUser?.id, assents);
  };

  const finishOnboarding = async () => {
    parentOnboarding.current?.classList.add('done');
    posthog?.capture('onboarding_complete');
    await updateCurrentUserInfo({
      isOnboarded: true,
    });
    navigate('/');
  };

  const handleTopicChange = (topicId: string, active: boolean) => {
    // Create a copy of selectedTopics
    const updatedTopics = cloneDeep(selectedTopics);

    // Find the topic in updatedTopics and update its active state
    const topic = updatedTopics.find((topic) => topic.id === topicId);
    if (topic) {
      topic.active = active;
      // Update all children recursively
      const updateChildrenActive = (children: TopicTreeNode[]) => {
        if (!children) return;
        children.forEach((child) => {
          child.active = active;
          if (child.children) {
            updateChildrenActive(child.children);
          }
        });
      };

      if (topic.children) {
        updateChildrenActive(topic.children);
      }
    }

    setSelectedTopics(updatedTopics);
  };

  const handleEnableAllTopics = () => {
    // Set all topics to active
    const allTopicsActive = cloneDeep(availableTopics).map((topic) => {
      // Set the top-level topic to active
      topic.active = true;

      // Set all children to active recursively
      const updateChildrenActive = (children: TopicTreeNode[]) => {
        if (!children) return;
        children.forEach((child) => {
          child.active = true;
          if (child.children) {
            updateChildrenActive(child.children);
          }
        });
      };

      if (topic.children) {
        updateChildrenActive(topic.children);
      }

      return topic;
    });

    setSelectedTopics(allTopicsActive);
  };

  const handleDisableAllTopics = () => {
    // Set all topics to inactive
    const allTopicsInactive = cloneDeep(availableTopics).map((topic) => {
      // Set the top-level topic to inactive
      topic.active = false;

      // Set all children to inactive recursively
      const updateChildrenActive = (children: TopicTreeNode[]) => {
        if (!children) return;
        children.forEach((child) => {
          child.active = false;
          if (child.children) {
            updateChildrenActive(child.children);
          }
        });
      };

      if (topic.children) {
        updateChildrenActive(topic.children);
      }

      return topic;
    });

    setSelectedTopics(allTopicsInactive);
  };

  const handleSaveTopics = async () => {
    if (isStepExam(primaryExam)) {
      const flatTopics = flattenTopicTree(selectedTopics);
      updateTopicTree(flatTopics, true);
      const allTopicsActive = selectedTopics.every((topic) => topic.active);
      const allTopicsInactive = selectedTopics.every((topic) => !topic.active);
      dispatch(
        setActiveFilters({
          allTopicsActive,
          allTopicsInactive,
          topicTree: selectedTopics,
        })
      );
    }
  };

  return (
    <>
      <div
        className={`onboarding ${primaryExam?.id ? 'exam-selected' : ''}`}
        ref={parentOnboarding}
      >
        <div className="frame-container">
          {/* 0 - Exam Selection */}
          <Frame
            classes="frame--exam-selection"
            frameId={0}
            currentOnboardingFrameId={frameSequence[currentFrameIndex]}
            currentFrameIndex={currentFrameIndex}
            setCurrentFrameIndex={setCurrentFrameIndex}
            frameSequence={frameSequence}
            showNext={Boolean(primaryExam?.id)}
            showBack={false}
            advanceAllowed={!!primaryExam?.id}
          >
            <div className="full-custom-frame">
              <div className="full-custom-frame_content">
                <div id="title">
                  <p id="stream-target">
                    Hi{' '}
                    {currentUser?.name
                      ? currentUser?.name.split(' ')[0]
                      : 'there'}
                    . Welcome to Ora.
                  </p>
                </div>
                <div className="exam-selection-container">
                  <h1>Choose an exam</h1>
                  <p>You can add more later.</p>
                  <ExamSelect
                    onClick={handleExamSelect}
                    addExam={handleAddExam}
                    updatePrimaryExam={updatePrimaryExam}
                  />
                </div>
              </div>
            </div>
          </Frame>
          {/* 1 - Exam Date */}
          <Frame
            classes="frame--exam-date"
            frameId={1}
            currentOnboardingFrameId={frameSequence[currentFrameIndex]}
            currentFrameIndex={currentFrameIndex}
            setCurrentFrameIndex={setCurrentFrameIndex}
            frameSequence={frameSequence}
            showNext={true}
            showBack={true}
            advanceAllowed={Boolean(
              !primaryExam?.examDate ||
                (moment(primaryExam?.examDate).isAfter(
                  moment.utc().subtract(4, 'years')
                ) &&
                  moment(primaryExam?.examDate).isBefore(
                    moment.utc().add(4, 'years')
                  ))
            )}
          >
            <div id="title">{primaryExam?.name}</div>
            <h1>Choose your exam date</h1>
            <p>Ora will schedule content based on your exam date and goal.</p>
            <CustomExamDatePicker
              value={useMemo(
                () =>
                  primaryExam?.examDate ? moment(primaryExam?.examDate) : null,
                [primaryExam?.examDate]
              )}
              onChange={(newDate) => {
                if (newDate && moment(newDate).isValid()) {
                  updateExamDate(moment(newDate).toDate());
                }
              }}
            />
          </Frame>
          {/* 2 - Exam Goal */}
          <Frame
            classes="frame--goal-selection"
            frameId={2}
            currentOnboardingFrameId={frameSequence[currentFrameIndex]}
            currentFrameIndex={currentFrameIndex}
            setCurrentFrameIndex={setCurrentFrameIndex}
            frameSequence={frameSequence}
            showNext={true}
            showBack={true}
            advanceAllowed={userSelectedGoal !== ''}
          >
            <div id="title">{primaryExam?.name}</div>
            <h1>Choose your workload</h1>
            <p>
              Your workload determines the baseline amount of content you will
              cover.
            </p>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}
            >
              <GoalSelection
                selectedGoal={userSelectedGoal}
                setSelectedGoal={setUserSelectedGoal}
                isUsingQbank={true}
                isUsingFlashcards={true}
                primaryExamName={primaryExam?.name}
                showCheckmark={true}
              />
            </div>
          </Frame>
          {/* 3 - Topics */}
          <Frame
            classes="frame--topic-selection"
            frameId={3}
            currentOnboardingFrameId={frameSequence[currentFrameIndex]}
            currentFrameIndex={currentFrameIndex}
            setCurrentFrameIndex={setCurrentFrameIndex}
            frameSequence={frameSequence}
            showNext={true}
            showBack={true}
            advanceAllowed={selectedTopics.some((topic) => topic.active)}
            onContinue={handleSaveTopics}
          >
            <div id="title">{primaryExam?.name}</div>
            <h1>Choose your topics</h1>
            <p className="m-b-2">
              Select the topics you want included in your sessions. You can
              change these later.
            </p>
            <div className="topic-selections">
              <div className="topic-selections_actions">
                <label>All Topics</label>
                <OraSwitch
                  checked={selectedTopics.every((topic) => topic.active)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleEnableAllTopics();
                    } else {
                      handleDisableAllTopics();
                    }
                  }}
                />
              </div>
              <div className="topic-selections_topics">
                {availableTopics.map((topic) => {
                  // Find the topic in selectedTopics and check its active property
                  const selectedTopic = selectedTopics.find(
                    (selectedTopic) => selectedTopic.id === topic.id
                  );
                  const isActive = selectedTopic ? selectedTopic.active : false;

                  return (
                    <div className="topic-wrapper" key={topic.id}>
                      <div className="topic-name">{topic.name}</div>
                      <OraSwitch
                        checked={isActive}
                        onChange={(e) =>
                          handleTopicChange(topic.id, e.target.checked)
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </Frame>
          {/* 4 - Strengths/Weaknesses */}
          <Frame
            classes="frame--strengths-weaknesses"
            frameId={4}
            currentOnboardingFrameId={frameSequence[currentFrameIndex]}
            currentFrameIndex={currentFrameIndex}
            setCurrentFrameIndex={setCurrentFrameIndex}
            frameSequence={frameSequence}
            showNext={true}
            showBack={true}
            advanceAllowed={true}
          >
            <div id="title">{primaryExam?.name}</div>
            <h1>Describe your weaknesses</h1>
            <p>
              Ora instantly personalizes your study plan based on your
              weaknesses. This is optional&mdash;Ora adapts as you study!
            </p>
            <div className="strengths-weaknesses">
              {/* <div>
                <label>My Strengths</label>
                <textarea
                  placeholder="For example: I'm really good at cardio and pulm, especially pathophys and Dx. I also feel pretty solid in nephrology, except acid-base disorders (how in the world do you calculate a delta gap)"
                  className="textarea"
                  value={strengths}
                  onChange={(e) => setStrengths(e.target.value)}
                  maxLength={1000}
                ></textarea>
              </div> */}
              <div>
                <label>My Weaknesses</label>
                <textarea
                  placeholder="For example: I mostly struggle with infectious disease Tx, histo slide interpretation, quality improvement, and biostats"
                  className="textarea"
                  value={weaknesses}
                  onChange={(e) => setWeaknesses(e.target.value)}
                  maxLength={1000}
                ></textarea>
              </div>
            </div>
          </Frame>
          {/* 6 - Choose School */}
          <Frame
            classes="frame--choose-school"
            frameId={6}
            currentOnboardingFrameId={frameSequence[currentFrameIndex]}
            currentFrameIndex={currentFrameIndex}
            setCurrentFrameIndex={setCurrentFrameIndex}
            frameSequence={frameSequence}
            showNext={true}
            showBack={currentUser?.ambassador ? false : true}
            advanceAllowed={userSchool !== null}
          >
            <h1>
              {currentUser?.ambassador
                ? 'Confirm your school'
                : 'Choose your school'}
            </h1>
            <SchoolSelector />
            {currentUser?.ambassador && (
              <div className="no-school">
                <p>Not your school?</p>
                <button
                  className="button button--link"
                  onClick={() => {
                    window.Intercom('showNewMessage');
                    hapticsImpactLight();
                  }}
                >
                  Message Us
                  <ArrowRight />
                </button>
              </div>
            )}
          </Frame>
          {/* 7 - Build School Exam */}
          <Frame
            classes="frame--build-school-exam"
            frameId={7}
            currentOnboardingFrameId={frameSequence[currentFrameIndex]}
            currentFrameIndex={currentFrameIndex}
            setCurrentFrameIndex={setCurrentFrameIndex}
            frameSequence={frameSequence}
            showNext={true}
            showBack={true}
            advanceAllowed={Boolean(
              primaryExam && isCustomSchoolExam(primaryExam)
            )}
          >
            <h1>Let's add your first exam</h1>
            <SchoolExamInfo
              isCreateMode={true}
              newExamCreated={newSchoolExamCreated}
              isOnboarding={true}
            />
          </Frame>
          {/* 8 - Add your lectures */}
          <Frame
            classes="frame--add-lectures"
            frameId={8}
            currentOnboardingFrameId={frameSequence[currentFrameIndex]}
            currentFrameIndex={currentFrameIndex}
            setCurrentFrameIndex={setCurrentFrameIndex}
            frameSequence={frameSequence}
            showNext={true}
            showBack={true}
            advanceAllowed={
              primaryExam?.examDate !== null && lectures.length > 0
            }
            advanceTooltip={'You must add at least one lecture.'}
          >
            <div id="title">{primaryExam?.name}</div>
            <h1>Add your lectures</h1>
            <p className="m-b-2">
              Ora AI aligns assignments with your lecture schedule. You can add
              or modify lectures later.
            </p>
            <LectureScheduleEditor />
          </Frame>
          {/* 9 - Show Verified Exam Details */}
          <Frame
            classes="frame--cohort-exam-details"
            frameId={9}
            currentOnboardingFrameId={frameSequence[currentFrameIndex]}
            currentFrameIndex={currentFrameIndex}
            setCurrentFrameIndex={setCurrentFrameIndex}
            frameSequence={frameSequence}
            showNext={false}
            showBack={false}
            advanceAllowed={true}
            hideStepIndicator={true}
          >
            <div className="full-custom-frame">
              <div className="full-custom-frame_content">
                <h1>{primaryExam?.name}</h1>
                <LectureScheduleEditor />
              </div>
              <div className="full-custom-frame_actions">
                <button
                  className="button button--secondary button--prev"
                  onClick={() => {
                    setCurrentFrameIndex(currentFrameIndex - 1);
                    hapticsImpactLight();
                  }}
                >
                  <ArrowLeft />
                </button>
                <button
                  className="button"
                  style={{ padding: '1rem 2rem' }}
                  onClick={() => {
                    setCurrentFrameIndex(currentFrameIndex + 1);
                    hapticsImpactLight();
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </Frame>
          {/* 10 - Exam Blocks */}
          <Frame
            classes="frame--exam-blocks"
            frameId={10}
            currentOnboardingFrameId={frameSequence[currentFrameIndex]}
            currentFrameIndex={currentFrameIndex}
            setCurrentFrameIndex={setCurrentFrameIndex}
            frameSequence={frameSequence}
            showNext={false}
            showBack={false}
            advanceAllowed={true}
            hideStepIndicator={false}
          >
            <ExamBlockBuilder
              setCurrentFrameIndex={setCurrentFrameIndex}
              cohortExams={cohortExams}
            />
          </Frame>
          {/* 100 - Loader */}
          <Frame
            frameId={100}
            currentOnboardingFrameId={frameSequence[currentFrameIndex]}
            currentFrameIndex={currentFrameIndex}
            setCurrentFrameIndex={setCurrentFrameIndex}
            frameSequence={frameSequence}
            showNext={false}
            showBack={false}
            advanceAllowed={false}
          >
            <h2 className="setting-up" ref={inputHeading}>
              Setting things up...
            </h2>
            <div className="inputs">
              <div ref={inputDiv1}>
                <ArrowRight />
                {primaryExam?.name}
              </div>
              <div ref={inputDiv2}>
                <ArrowRight />
                {primaryExam?.examDate
                  ? moment(primaryExam.examDate).format('MMMM D, YYYY') +
                    ' exam date'
                  : 'No exam date'}
              </div>
            </div>
            <hr ref={hrRef} />
            <div className="automatons" ref={autoSection}>
              <div className="automaton" ref={auto1}>
                <CircularProgress
                  sx={{
                    color: 'var(--type-heading)',
                    height: '1rem !important',
                    width: '1rem !important',
                  }}
                />
                <Check className="check" />
                {strengths.trim() === '' && weaknesses.trim() === ''
                  ? 'Initializing your study profile'
                  : 'Analyzing your weaknesses'}
              </div>
              <div className="automaton" ref={auto2}>
                <CircularProgress
                  sx={{
                    color: 'var(--type-heading)',
                    height: '1rem !important',
                    width: '1rem !important',
                  }}
                />
                <Check className="check" />
                Calculating your workload
              </div>
              <div className="automaton" ref={auto3}>
                <CircularProgress
                  sx={{
                    color: 'var(--type-heading)',
                    height: '1rem !important',
                    width: '1rem !important',
                  }}
                />
                <Check className="check" />
                Generating your study plan
              </div>
            </div>
          </Frame>
        </div>
        <div className="graphic-container">
          <div className="graphic-container_orb-wrapper">
            <ThreeOrb isScreensaver={false} />
          </div>
        </div>
        {isImpersonating && (
          <div
            style={{
              padding: '1rem',
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              backgroundColor: 'red',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
            onClick={handleStopImpersonating}
          >
            <Crosshair color="white" />
          </div>
        )}
        <div className={`flash-loader ${hideFlashLoader ? 'hide' : ''}`}></div>
      </div>
    </>
  );
};

export default Onboarding;
